import {
  FC, memo, useCallback, useMemo,
} from 'react';
import { useAppSelector } from 'app/providers/Store';
import { IStore } from 'app/providers/Store/model/store';
import { Modal } from 'shared/ui/Modal';
import { Form } from 'features/Form';
import dayjs from 'dayjs';
import { EngineRequestDto } from 'shared/api';
import { EngineDetail } from 'entities/engine/model/types/types';
import { ENGINE_FORM_FIELDS } from '../../constants/engineConstants';
import { useEngines } from '../../hooks/useEngines';

interface EngineFormProps {
  isOpen: boolean;
  isEditing: boolean;
  onSubmit?: (data: Record<string, unknown>) => void;
  onCancel: () => void;
  engineId?: number | null;
  onSuccess?: () => void;
  engineDetail?: EngineDetail;
}

export const EngineForm: FC<EngineFormProps> = memo(({
  isOpen,
  isEditing,
  onSubmit,
  onCancel,
  engineId,
  onSuccess,
  engineDetail,
}) => {
  const { createNewEngine, updateExistingEngine } = useEngines();
  const { user } = useAppSelector((state: IStore) => state.userSlice);

  const effectiveEngineId = useMemo(() => engineId ?? (engineDetail?.id ?? null), [engineId, engineDetail]);

  const computedInitialValues = useMemo(() => {
    if (!engineDetail) return {};
    return {
      orderCode: engineDetail.orderCode,
      serialNumber: engineDetail.serialNumber,
      engineType: engineDetail.engineType,
      comment: engineDetail.comment,
      lastMaintenance: engineDetail.lastMaintenance ? dayjs(engineDetail.lastMaintenance) : undefined,
      plannedDateOfStop: engineDetail.plannedDateOfStop ? dayjs(engineDetail.plannedDateOfStop) : undefined,
      maintenanceIntervalHours: engineDetail.maintenanceIntervalHours,
      nameplatePhotoUuids: (engineDetail as unknown as { nameplatePhotoUuids?: string[] })?.nameplatePhotoUuids,
    } as Record<string, unknown>;
  }, [engineDetail]);

  const handleInternalSubmit = useCallback(async (data: Record<string, unknown>) => {
    if (onSubmit) {
      onSubmit(data);
      return;
    }

    const payload: EngineRequestDto = {
      orderCode: data.orderCode as string | undefined,
      serialNumber: data.serialNumber as string | undefined,
      engineType: data.engineType as unknown as EngineRequestDto['engineType'] | undefined,
      comment: data.comment as string | undefined,
      nameplatePhotoUuids: data.nameplatePhotoUuids as string[] | undefined,
      maintenanceIntervalHours: data.maintenanceIntervalHours as number | undefined,
      userId: user.id,
      lastMaintenance: (data.lastMaintenance && dayjs(data.lastMaintenance as unknown as dayjs.Dayjs).isValid())
        ? dayjs(data.lastMaintenance as unknown as dayjs.Dayjs).format('YYYY-MM-DDTHH:mm:ss')
        : undefined,
      plannedDateOfStop: (data.plannedDateOfStop && dayjs(data.plannedDateOfStop as unknown as dayjs.Dayjs).isValid())
        ? dayjs(data.plannedDateOfStop as unknown as dayjs.Dayjs).format('YYYY-MM-DDTHH:mm:ss')
        : undefined,
    };

    const result = isEditing && effectiveEngineId
      ? await updateExistingEngine(effectiveEngineId, payload)
      : await createNewEngine(payload);

    if (result.success) {
      onSuccess?.();
      onCancel();
    }
  }, [onSubmit, isEditing, engineId, updateExistingEngine, createNewEngine, user]);

  return (
    <Modal
      title={isEditing ? 'Редактировать электрическую машину' : 'Добавить электрическую машину'}
      open={isOpen}
      onCancel={onCancel}
      width={1200}
      footer={null}
      destroyOnClose
    >
      <Form
        fields={ENGINE_FORM_FIELDS}
        initialValues={computedInitialValues}
        onSubmit={handleInternalSubmit}
        columns={2}
      />
    </Modal>
  );
});

EngineForm.displayName = 'EngineForm';
